// WithdrawalConfirm.jsx — Refund Confirm
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FaArrowLeft, FaEye, FaEyeSlash, FaLock, FaUndo } from "react-icons/fa";
import { Withdraw } from "../services/api";
import DashboardNavbar from "../components/layout/DashboardNavbar";

export function WithdrawalConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { withdrawalDetails, walletBalance } = location.state || {};

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  const REFUND_FEE = 200;

  if (!withdrawalDetails) {
    navigate("/withdraw");
    return null;
  }

  const amount = parseFloat(withdrawalDetails.amount);
  const totalDebit = amount + REFUND_FEE;

  const handleConfirm = async () => {
    if (!password || password.length < 6) {
      setError("Please enter your password (minimum 6 characters).");
      return;
    }
    setError("");
    setIsProcessing(true);

    try {
      const payload = {
        payer_first_name: withdrawalDetails.payer_first_name,
        payer_last_name: withdrawalDetails.payer_last_name,
        payer_email: withdrawalDetails.payer_email,
        payer_phone_number: withdrawalDetails.payer_phone_number,
        recipient_first_name: withdrawalDetails.recipient_first_name,
        recipient_last_name: withdrawalDetails.recipient_last_name,
        recipient_bank_name: withdrawalDetails.recipient_bank_name,
        recipient_account_number: withdrawalDetails.recipient_account_number,
        recipient_account_name: withdrawalDetails.recipient_account_name,
        amount,
        fee: REFUND_FEE,
        password,
        transaction_description: withdrawalDetails.transaction_description,
      };

      const response = await Withdraw(payload);

      navigate("/wallet/withdraw/receipt", {
        state: { status: response.status || "pending", data: response, withdrawalDetails, walletBalance },
      });
    } catch (err) {
      navigate("/wallet/withdraw/receipt", {
        state: { status: "failed", data: { message: err.message || "Refund request failed" }, withdrawalDetails, walletBalance },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DashboardNavbar />
      <main className="w-full max-w-md mx-auto px-4 py-10">

        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 shadow-sm">
            <FaArrowLeft className="text-gray-600 w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FaUndo className="text-green-600" /> Confirm Refund
            </h1>
            <p className="text-sm text-gray-500">Review and confirm your refund request</p>
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5 space-y-4">
          <div className="text-center pb-4 border-b border-gray-100">
            <p className="text-sm text-gray-500 mb-1">Refund Amount</p>
            <p className="text-4xl font-bold text-green-700">₦{amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
          </div>

          <div className="space-y-2 text-sm">
            {[
              ["Bank", withdrawalDetails.recipient_bank_name],
              ["Account Number", withdrawalDetails.recipient_account_number],
              ["Account Name", withdrawalDetails.recipient_account_name],
              ["Refund Fee", `₦${REFUND_FEE.toLocaleString()}`],
              ["Total Requested", `₦${totalDebit.toLocaleString()}`],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <span className="text-gray-500">{label}</span>
                <span className="font-semibold text-gray-900">{value}</span>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-3 text-xs text-yellow-700">
            ⚠️ This refund request requires Super Admin approval. Processing time may vary.
          </div>
        </div>

        {/* Password */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <FaLock className="text-gray-400" /> Confirm with your password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl pr-12 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter your account password"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400">
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        </div>

        <div className="flex gap-3">
          <Button onClick={() => navigate(-1)} variant="outline" className="flex-1 rounded-xl h-12">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isProcessing || !password.trim()}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl h-12 font-semibold disabled:opacity-50"
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Processing...
              </span>
            ) : "Submit Refund Request"}
          </Button>
        </div>
      </main>
    </div>
  );
}
